import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaBuilding, FaArrowLeft, FaMapMarkerAlt, FaBed, FaUser, FaEnvelope, FaPhone, FaTag, FaCheckCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../pages_premium.css';
import { getFullImageUrl } from '../utils/url';

function PropertyView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(0);

    useEffect(() => {
        if (property && property.PropertyImages && property.PropertyImages.length > 1) {
            const interval = setInterval(() => {
                setSelectedImage(prev => (prev + 1) % property.PropertyImages.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [property]);

    useEffect(() => {
        fetchProperty();
    }, [id]);

    const fetchProperty = async () => {
        try {
            const response = await axiosInstance.get(`/properties/${id}`);
            setProperty(response.data);
        } catch (error) {
            toast.error('Failed to load property details');
            navigate('/properties');
        } finally {
            setLoading(false);
        }
    };

    const getImageUrl = (image) => {
        if (!image) return '';
        const url = image.image_url || image;
        return getFullImageUrl(url);
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return '₹' + Number(price).toLocaleString('en-IN');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'available': return <span className="ptag ptag-student" style={{ background: '#d4edda', color: '#155724' }}>Available</span>;
            case 'rented': return <span className="ptag ptag-student" style={{ background: '#ffeeba', color: '#856404' }}>Rented</span>;
            case 'inactive': return <span className="ptag ptag-student" style={{ background: '#f8d7da', color: '#721c24' }}>Inactive</span>;
            default: return <span className="ptag ptag-student">{status || 'N/A'}</span>;
        }
    };

    const getListingRole = (role) => {
        switch (role) {
            case 'student': return 'Student Property';
            case 'owner': return 'Private Owner';
            case 'agent': return 'Real Estate Agent';
            default: return role || 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                        <p style={{ marginTop: '16px', color: '#7a87a3', fontWeight: 600 }}>Loading property details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!property) return null;

    const images = property.PropertyImages || [];
    
    const safeParse = (val) => {
        if (!val) return null;
        let curr = val;
        while (typeof curr === 'string') {
            try {
                const parsed = JSON.parse(curr);
                if (parsed === curr) break;
                curr = parsed;
            } catch (e) { break; }
        }
        return curr;
    };

    const rawAboutHome = safeParse(property.about_home) || {};
    const rawAboutRoom = safeParse(property.about_room) || {};
    const rawRentAndAvailability = safeParse(property.rent_and_availability) || {};
    const owner = property.User || null;

    // Extract description from JSON sub-fields if main description is empty
    let mainDescription = property.description;

    const aboutHome = { ...rawAboutHome };
    if (aboutHome.description) {
        if (!mainDescription) mainDescription = aboutHome.description;
        delete aboutHome.description;
    }
    if (aboutHome.desc) {
        if (!mainDescription) mainDescription = aboutHome.desc;
        delete aboutHome.desc;
    }

    // Extract who_living_with
    let whoLivingWith = aboutHome.who_living_with || aboutHome.whoLivingWith || '';
    delete aboutHome.who_living_with;
    delete aboutHome.whoLivingWith;

    const aboutRoom = { ...rawAboutRoom };
    if (aboutRoom.description) {
        if (!mainDescription) mainDescription = aboutRoom.description;
        delete aboutRoom.description;
    }
    if (aboutRoom.desc) {
        if (!mainDescription) mainDescription = aboutRoom.desc;
        delete aboutRoom.desc;
    }

    // Extract rooms list
    const roomsList = aboutRoom.rooms || aboutRoom.Rooms || [];
    delete aboutRoom.rooms;
    delete aboutRoom.Rooms;

    const rentAndAvailability = { ...rawRentAndAvailability };
    if (rentAndAvailability.description) {
        if (!mainDescription) mainDescription = rentAndAvailability.description;
        delete rentAndAvailability.description;
    }
    if (rentAndAvailability.desc) {
        if (!mainDescription) mainDescription = rentAndAvailability.desc;
        delete rentAndAvailability.desc;
    }

    const renderJsonSection = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return <p style={{ color: '#aab4c8', fontStyle: 'italic', margin: 0 }}>No details available.</p>;
        }
        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                <style>{`
                    .detail-item-card {
                        background: white;
                        border: 1px solid #eef1f8;
                        border-radius: 12px;
                        padding: 8px 12px;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.02);
                        transition: all 0.25s ease;
                    }
                    .detail-item-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0,0,0,0.06) !important;
                        border-color: #4facfe !important;
                    }
                `}</style>
                {Object.entries(data).map(([k, v], idx) => {
                    const formattedKey = k.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    
                    if (v === undefined || v === null || v === '') return null;

                    // If value is a nested object (e.g. Accessibility)
                    if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
                        const activeFeatures = Object.entries(v)
                            .filter(([, val]) => val === true || val === 'true')
                            .map(([key]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
                        
                        return (
                            <div key={idx} className="detail-item-card" style={{ gridColumn: 'span 2' }}>
                                <div style={{ color: '#7a87a3', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {formattedKey}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                    {activeFeatures.length > 0 ? activeFeatures.map((item, i) => (
                                        <span key={i} style={{ background: '#eef1f8', color: '#3a4660', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 }}>
                                            ✓ {item}
                                        </span>
                                    )) : <span style={{ color: '#aab4c8', fontSize: '0.85rem' }}>None specified</span>}
                                </div>
                            </div>
                        );
                    }

                    // If value is an array or a comma-separated string
                    const isArray = Array.isArray(v);
                    const isCommaString = typeof v === 'string' && v.includes(',') && v.split(',').length > 1;

                    if (isArray || isCommaString) {
                        const itemsList = isArray ? v : v.split(',').map(item => item.trim()).filter(Boolean);
                        
                        return (
                            <div key={idx} className="detail-item-card" style={{ gridColumn: 'span 2' }}>
                                <div style={{ color: '#7a87a3', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {formattedKey}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                    {itemsList.map((item, i) => (
                                        <span key={i} style={{ background: '#eef1f8', color: '#3a4660', padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 }}>
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    // Standard single value
                    let formattedVal = v;
                    if (v === true) formattedVal = 'Yes';
                    else if (v === false) formattedVal = 'No';
                    else formattedVal = String(v);

                    return (
                        <div key={idx} className="detail-item-card">
                            <div style={{ color: '#7a87a3', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {formattedKey}
                            </div>
                            <div style={{ color: '#1a2236', fontSize: '0.92rem', fontWeight: 800, marginTop: '4px', wordBreak: 'break-word' }}>
                                {formattedVal}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties" style={{ padding: '12px 20px', marginBottom: '14px', alignItems: 'center' }}>
                    <div className="ph-blob ph-blob-1" style={{ width: '140px', height: '140px', top: '-50px', right: '40px' }}></div>
                    <div className="ph-blob ph-blob-2" style={{ width: '90px', height: '90px', bottom: '-40px', right: '200px' }}></div>
                    <div className="ph-left" style={{ gap: '12px' }}>
                        <div className="ph-icon" style={{ width: '44px', height: '44px', borderRadius: '12px', fontSize: '20px' }}>
                            <FaBuilding size={22} />
                        </div>
                        <div>
                            <h1 className="ph-title" style={{ fontSize: '1.3rem', margin: '0 0 2px 0', lineHeight: 1.2 }}>{property.title || 'Property Details'}</h1>
                            <p className="ph-subtitle" style={{ fontSize: '0.82rem', margin: 0 }}>
                                <FaMapMarkerAlt style={{ marginRight: 6, color: '#dc2626' }} />
                                {property.address}
                            </p>
                        </div>
                    </div>
                    <div className="ph-right">
                        <button 
                            className="premium-cancel-btn" 
                            onClick={() => navigate('/properties')} 
                            style={{ 
                                background: '#f1f5f9', 
                                color: '#334155', 
                                border: '1px solid #cbd5e1', 
                                padding: '8px 16px', 
                                fontSize: '0.85rem', 
                                fontWeight: 600,
                                borderRadius: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <FaArrowLeft style={{ marginRight: '6px' }} /> Back to Properties
                        </button>
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    
                    {/* Image Gallery */}
                    <div className="premium-table-card p-4">
                        <div className="property-gallery-hero" style={{ borderRadius: '16px', overflow: 'hidden', marginBottom: '12px', background: '#f1f4f9', position: 'relative', height: '420px' }}>
                            {images.length > 0 ? (
                                <>
                                    <img
                                        src={getImageUrl(images[selectedImage])}
                                        alt="Property"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    {images.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setSelectedImage(prev => (prev === 0 ? images.length - 1 : prev - 1))}
                                                style={{
                                                    position: 'absolute', top: '50%', left: '20px', transform: 'translateY(-50%)',
                                                    background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%',
                                                    width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', color: '#1a2236', fontSize: '1rem', zIndex: 10
                                                }}
                                            >
                                                <FaChevronLeft />
                                            </button>
                                            <button
                                                onClick={() => setSelectedImage(prev => (prev + 1) % images.length)}
                                                style={{
                                                    position: 'absolute', top: '50%', right: '20px', transform: 'translateY(-50%)',
                                                    background: 'rgba(255, 255, 255, 0.9)', border: 'none', borderRadius: '50%',
                                                    width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', color: '#1a2236', fontSize: '1rem', zIndex: 10
                                                }}
                                            >
                                                <FaChevronRight />
                                            </button>
                                        </>
                                    )}
                                </>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aab4c8' }}>
                                    <FaBuilding size={60} />
                                </div>
                            )}
                        </div>
                        {images.length > 1 && (
                            <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '8px' }}>
                                {images.map((img, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => setSelectedImage(idx)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedImage(idx); }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`View image ${idx + 1}`}
                                        style={{
                                            width: '80px', height: '56px', borderRadius: '8px', overflow: 'hidden',
                                            cursor: 'pointer', border: selectedImage === idx ? '2px solid #dc2626' : '2px solid transparent',
                                            flexShrink: 0, transition: 'all 0.2s ease', opacity: selectedImage === idx ? 1 : 0.6
                                        }}
                                    >
                                        <img src={getImageUrl(img)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Price, Status & Quick Overview Actions Bar */}
                    <div className="premium-table-card p-4">
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                <div style={{ background: '#fff0f0', border: '1px solid #ffe0e0', borderRadius: '14px', padding: '8px 18px', display: 'inline-flex', flexDirection: 'column', minWidth: '160px' }}>
                                    <span style={{ fontSize: '0.8rem', color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>Rent Amount</span>
                                    <strong style={{ fontSize: '1.8rem', fontWeight: 900, color: '#dc2626', marginTop: '2px' }}>{formatPrice(property.rent)}</strong>
                                    <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>per week</span>
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span style={{ color: '#7a87a3', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Status</span>
                                    <div>{getStatusBadge(property.status)}</div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span style={{ color: '#7a87a3', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Property Type</span>
                                    <strong style={{ color: '#1a2236', fontSize: '1.1rem', textTransform: 'capitalize' }}>{property.offerType || 'N/A'}</strong>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span style={{ color: '#7a87a3', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Category</span>
                                    <strong style={{ color: '#1a2236', fontSize: '1.1rem' }}>{getListingRole(property.listing_role)}</strong>
                                </div>

                                {(property.minStay || property.maxStay) && (
                                    <div style={{ display: 'flex', gap: '20px' }}>
                                        {property.minStay && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span style={{ color: '#7a87a3', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Min Stay</span>
                                                <strong style={{ color: '#1a2236', fontSize: '1.1rem' }}>{property.minStay}</strong>
                                            </div>
                                        )}
                                        {property.maxStay && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span style={{ color: '#7a87a3', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>Max Stay</span>
                                                <strong style={{ color: '#1a2236', fontSize: '1.1rem' }}>{property.maxStay}</strong>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <button
                                    className="premium-submit-btn"
                                    style={{ padding: '10px 24px', fontSize: '0.9rem' }}
                                    onClick={() => navigate(`/properties/edit/${property.id}`)}
                                >
                                    <FaBuilding style={{ marginRight: '6px' }} /> Edit Property
                                </button>
                                <button
                                    className="premium-cancel-btn"
                                    style={{ padding: '10px 24px', fontSize: '0.9rem' }}
                                    onClick={() => navigate('/properties')}
                                >
                                    <FaArrowLeft style={{ marginRight: '6px' }} /> Back to List
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Description Card */}
                    <div className="premium-table-card p-4">
                        <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaTag style={{ color: '#4facfe' }} /> Description
                        </h5>
                        <div 
                            style={{ 
                                background: '#f8faff', 
                                border: '1px solid #eef1f8', 
                                borderRadius: '14px', 
                                padding: '14px 18px', 
                                color: '#3a4660', 
                                lineHeight: '1.6', 
                                fontSize: '0.95rem',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.02)'
                            }} 
                            className="custom-description-scrollbar"
                        >
                            <style>{`
                                .custom-description-scrollbar::-webkit-scrollbar {
                                    width: 6px;
                                }
                                .custom-description-scrollbar::-webkit-scrollbar-track {
                                    background: #f1f4f9;
                                    border-radius: 8px;
                                }
                                .custom-description-scrollbar::-webkit-scrollbar-thumb {
                                    background: #c3cfd9;
                                    border-radius: 8px;
                                }
                                .custom-description-scrollbar::-webkit-scrollbar-thumb:hover {
                                    background: #a1b0be;
                                }
                            `}</style>
                            {mainDescription || 'No description provided.'}
                        </div>
                    </div>

                    {/* Who Living With Section */}
                    {whoLivingWith && (
                        <div className="premium-table-card p-4">
                            <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaUser style={{ color: '#4facfe' }} /> Who You&apos;ll Live With
                            </h5>
                            <div 
                                style={{ 
                                    background: '#f8faff', 
                                    border: '1px solid #eef1f8', 
                                    borderRadius: '14px', 
                                    padding: '12px 16px', 
                                    color: '#3a4660', 
                                    lineHeight: '1.6', 
                                    fontSize: '0.95rem',
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.02)'
                                }} 
                                className="custom-description-scrollbar"
                            >
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {whoLivingWith.split(/[\n,]/).map(s => s.trim()).filter(Boolean).map((person, idx) => (
                                        <span key={idx} style={{ 
                                            background: 'white', 
                                            border: '1px solid #eef1f8',
                                            color: '#3a4660', 
                                            padding: '5px 12px', 
                                            borderRadius: '50px', 
                                            fontSize: '0.85rem', 
                                            fontWeight: 700,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.01)',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}>
                                            👥 {person}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Room Configurations Section */}
                    {roomsList && roomsList.length > 0 && (
                        <div className="premium-table-card p-4">
                            <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaBed style={{ color: '#4facfe' }} /> Room Configurations
                            </h5>
                            <div style={{ display: 'grid', gridTemplateColumns: roomsList.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                                {roomsList.map((room, idx) => (
                                    <div key={idx} style={{ 
                                        background: '#f8faff', 
                                        border: '1px solid #eef1f8', 
                                        borderRadius: '14px', 
                                        padding: '14px 16px', 
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.02)' 
                                    }}>
                                        <div style={{ fontWeight: 800, color: '#1a2236', fontSize: '1.05rem', borderBottom: '1px solid #eef1f8', paddingBottom: '6px', marginBottom: '10px' }}>
                                            🛏️ {room.roomName || `Room ${idx + 1}`}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
                                            {room.roomType && <div><span style={{ color: '#7a87a3' }}>Type:</span> <strong style={{ color: '#3a4660' }}>{room.roomType}</strong></div>}
                                            {room.furnished && <div><span style={{ color: '#7a87a3' }}>Furnished:</span> <strong style={{ color: '#3a4660' }}>{room.furnished}</strong></div>}
                                            {room.rent && <div><span style={{ color: '#7a87a3' }}>Rent:</span> <strong style={{ color: '#dc2626' }}>₹{room.rent}/wk</strong></div>}
                                            {room.bond && <div><span style={{ color: '#7a87a3' }}>Bond:</span> <strong style={{ color: '#3a4660' }}>{room.bond}wk</strong></div>}
                                            {room.bedSize && <div><span style={{ color: '#7a87a3' }}>Bed Size:</span> <strong style={{ color: '#3a4660' }}>{room.bedSize}</strong></div>}
                                            {room.minStay && <div><span style={{ color: '#7a87a3' }}>Min Stay:</span> <strong style={{ color: '#3a4660' }}>{room.minStay}</strong></div>}
                                        </div>
                                        {room.roomFeatures && (Array.isArray(room.roomFeatures) ? room.roomFeatures.length > 0 : typeof room.roomFeatures === 'string') && (
                                            <div style={{ marginTop: '10px', borderTop: '1px dashed #eef1f8', paddingTop: '8px' }}>
                                                <div style={{ color: '#7a87a3', fontSize: '0.75rem', fontWeight: 700, marginBottom: '4px' }}>FEATURES</div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {(Array.isArray(room.roomFeatures) ? room.roomFeatures : room.roomFeatures.split(',')).map((f, i) => (
                                                        <span key={i} style={{ background: '#eef1f8', color: '#3a4660', padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 700 }}>
                                                            {f.trim()}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* About Home Grid */}
                    <div className="premium-table-card p-4">
                        <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCheckCircle style={{ color: '#4facfe' }} /> About Home
                        </h5>
                        {renderJsonSection(aboutHome)}
                    </div>

                    {/* About Room Grid */}
                    <div className="premium-table-card p-4">
                        <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCheckCircle style={{ color: '#4facfe' }} /> About Room
                        </h5>
                        {renderJsonSection(aboutRoom)}
                    </div>

                    {/* Rent & Availability Grid */}
                    <div className="premium-table-card p-4">
                        <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaCheckCircle style={{ color: '#4facfe' }} /> Rent & Availability
                        </h5>
                        {renderJsonSection(rentAndAvailability)}
                    </div>

                    {/* Owner Details Section */}
                    {owner && (
                        <div className="premium-table-card p-4">
                            <h5 style={{ fontWeight: 700, color: '#1a2236', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaUser style={{ color: '#4facfe' }} /> Owner Information
                            </h5>
                            <div style={{ background: '#f8faff', border: '1px solid #eef1f8', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    {owner.avatar ? (
                                        <img src={getFullImageUrl(owner.avatar)} alt="" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '2px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                                    ) : (
                                        <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'linear-gradient(135deg,#4facfe,#00b4d8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '1.3rem', border: '2px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                            {owner.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ fontWeight: 800, color: '#1a2236', fontSize: '1.1rem' }}>{owner.name || 'N/A'}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#7a87a3', fontWeight: 600 }}>Owner ID: #{owner.id}</div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                                    {owner.email && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#3a4660', fontWeight: 500, fontSize: '0.9rem' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4facfe', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}><FaEnvelope /></div>
                                            {owner.email}
                                        </div>
                                    )}
                                    {owner.phone_number && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#3a4660', fontWeight: 500, fontSize: '0.9rem' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0ab68e', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}><FaPhone /></div>
                                            {owner.phone_number}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default PropertyView;